import { QuoteGetResponse } from '@lio/api-types';
import { useEffect, useMemo } from 'react';
import { QuoteStatusChangeItem } from '@models/quoteStatusChange';
import { PortalMessage } from '@components/portalMessage/PortalMessage';
import Loading from '@components/Loading';
import { useSearchQuoteStatusChange } from '@services/quotes';
import { QuoteStatus } from '@models/quotes';
import { DownloadApplicationButton, yellowTextButtonStyles } from '@components/button/DownloadApplicationButton';
import { Box } from '@mui/material';
import { PortalMessageType } from '@components/portalMessage/PortalMessageType.enum';

export const CanceledQuote = ({ quote }: { quote: QuoteGetResponse }) => {
  const { quoteStatusChange, fetchNextPage } = useSearchQuoteStatusChange({
    quoteId: quote.id,
    pageLimit: 10,
  });
  const allStatusChanges = quoteStatusChange?.pages.flatMap<QuoteStatusChangeItem>((page) => page.items);
  const reasons = useMemo(() => {
    if (allStatusChanges) {
      return allStatusChanges
        ?.filter((el) => el.newQuoteStatus.id === QuoteStatus.Canceled.id)
        .reduce((arr, item) => {
          if (item.quoteStatusChangeReason?.reason) {
            arr.push(item.quoteStatusChangeReason.reason);
          }
          return arr;
        }, [] as string[]);
    }
    return [];
  }, [allStatusChanges]);

  useEffect(() => {
    const totalItems = quoteStatusChange?.pages[0].pagination.total ?? 0;
    const currentTotalItems = allStatusChanges?.length ?? 0;
    if (totalItems > currentTotalItems) {
      fetchNextPage();
    }
  }, [fetchNextPage, quoteStatusChange, allStatusChanges]);

  if (!quote) return <Loading />;

  return (
    <PortalMessage
      type={PortalMessageType.Canceled}
      title={['This quote has been cancelled']}
      subtitle={
        reasons.length > 0
          ? [<span key='reason-text'>This quote has been cancelled for the following reason(s):</span>]
          : []
      }
      secondarySubtitle={[
        <Box key='contact-text' component='span' sx={{ display: 'flex', flexDirection: 'column' }}>
          If you have questions, please contact your LIO Underwriter.
          <span>
            You may also
            <DownloadApplicationButton
              label='download your application'
              quoteId={quote.id}
              buttonProps={{
                color: 'secondary',
                variant: 'text',
                disableRipple: true,
                sx: yellowTextButtonStyles,
              }}
              quoteUpdatedDate={quote?.updatedAt}
            />
            for your records.
          </span>
        </Box>,
      ]}
      primaryBtnText='Message Underwriter'
      primaryBtnAction={`/quote/${quote.id}?viewReadOnly=True`}
      secondaryBtnText='Continue to your Homepage'
      secondaryBtnAction='/'
      items={reasons}
      quote={quote}
    />
  );
};
